import React, { useState } from "react"
import useTitle from "@/hooks/useTitle"
import { addIcon } from "@/assets/img/imgAssets"
import {
  PlusIcon,
  MinusIcon,
  TrashIcon,
  PencilSquareIcon,
} from "@heroicons/react/24/outline"
import {
  BuildingOffice2Icon,
  CalendarDaysIcon,
  MapPinIcon,
} from "@heroicons/react/24/solid"
import PulseLoader from "react-spinners/PulseLoader"
import { useGetOrganizationBUsQuery } from "./organizationBUsApiSlice"

const EtOrganizationBUList = ({
  setActiveAdminView,
  onAdd,
  onUpdate,
  onDelete,
  setSelectedEntityId,
  userOrgId = "",
}) => {
  // First Created - 27 Apr 2026
  // Author - Atul Pandey
  // Purpose - To list Organization BUs for the logged in user's Organization

  useTitle("Organization BUs List")

  const [showDetails, setShowDetails] = useState({})

  const {
    data: organizationBUsData,
    isError,
    error,
    isLoading,
  } = useGetOrganizationBUsQuery(userOrgId)

  const handleToggleDetails = (organizationBUId) => {
    setShowDetails((prev) => ({
      ...prev,
      [organizationBUId]: !prev[organizationBUId],
    }))
  }

  const createMarkup = (htmlContent) => {
    return { __html: htmlContent }
  }

  if (isLoading) return <PulseLoader color={"#ea580c"} />

  if (isError) {
    return (
      <div className="text-sm text-orange-700">
        Error occurred: {error?.data?.message}
      </div>
    )
  }

  const organizationBUs =
    organizationBUsData?.ids?.map((id) => {
      const organizationBU = organizationBUsData?.entities[id]

      const {
        _id,
        organizationBUName,
        organizationBUCode,
        organizationBUDesc,
        organizationBUPurpose,
        organizationBUIntro,
        organizationBUOutro,
        organizationBULocations,
        organizationBUTargetAudience,
        organizationBUTeamSize,
        organizationBURevenueSize,
        organizationBUStartedDate,
        organizationBURecognitions,
        organizationBUContributions,
        active,
      } = organizationBU

      return {
        _id,
        organizationBUName,
        organizationBUCode,
        organizationBUDesc,
        organizationBUPurpose,
        organizationBUIntro,
        organizationBUOutro,
        organizationBULocations,
        organizationBUTargetAudience,
        organizationBUTeamSize,
        organizationBURevenueSize,
        organizationBUStartedDate,
        organizationBURecognitions,
        organizationBUContributions,
        active,
      }
    }) || []

  return (
    <div className="bg-gray-200 m-1 p-1 rounded font-poppins mx-auto">
      {/* Header */}
      <div className="flex justify-start items-center m-1 p-1">
        <div>
          <p className="text-2xl m-1 p-1 font-bold">Organization BUs</p>
        </div>

        <div>
          <img
            src={addIcon}
            alt=""
            className="h-10 w-10 cursor-pointer"
            onClick={onAdd}
          />
        </div>
      </div>

      {organizationBUs?.length === 0 && (
        <div className="bg-white rounded m-1 p-4">
          <p className="text-sm text-gray-600">
            No Organization BUs found. Click the add icon to create one.
          </p>
        </div>
      )}

      {organizationBUs?.map((item, index) => (
        <div key={item._id} className="m-1 p-1 bg-white rounded">
          <div className="m-1 p-1 bg-gray-100 rounded">
            <div className="flex justify-between items-center">
              <div className="flex justify-start items-center mx-2">
                <div
                  onClick={() => handleToggleDetails(item._id)}
                  className="m-1 p-1 cursor-pointer rounded-full h-7 w-7 flex justify-center items-center hover:bg-white"
                >
                  {showDetails[item._id] ? (
                    <MinusIcon className="h-6 w-6 text-orange-600" />
                  ) : (
                    <PlusIcon className="h-4 w-4 text-orange-600" />
                  )}
                </div>

                <div className="m-1 p-1">
                  <div className="flex justify-start items-center">
                    <p className="font-bold">
                      {index + 1} - {item.organizationBUName}
                    </p>

                    {item.organizationBUCode && (
                      <p className="text-[10px] bg-orange-100 text-orange-700 rounded-full mx-2 px-2 py-1">
                        {item.organizationBUCode}
                      </p>
                    )}

                    {!item.active && (
                      <p className="text-[10px] bg-red-100 text-red-700 rounded-full mx-2 px-2 py-1">
                        Inactive
                      </p>
                    )}
                  </div>

                  <p className="text-xs">{item.organizationBUDesc}</p>
                </div>
              </div>

              <div className="flex justify-start items-center mx-2">
                <div className="rounded-full flex justify-center items-center h-6 w-6 hover:bg-white">
                  <PencilSquareIcon
                    onClick={() => onUpdate(item?._id)}
                    className="h-4 w-4 text-orange-600 cursor-pointer"
                  />
                </div>

                <div className="rounded-full flex justify-center items-center h-6 w-6 hover:bg-white">
                  <TrashIcon
                    onClick={() => onDelete(item?._id)}
                    className="h-4 w-4 text-orange-600 cursor-pointer"
                  />
                </div>
              </div>
            </div>

            {showDetails[item._id] && (
              <div className="m-1 p-1 bg-gray-200 rounded">
                <div className="m-1 p-1 rounded bg-white">
                  {item?.organizationBUIntro && (
                    <div className="bg-gray-50 rounded text-xs mx-2 px-2 my-1 py-1">
                      <p
                        className="m-1 p-1"
                        dangerouslySetInnerHTML={createMarkup(
                          item?.organizationBUIntro
                        )}
                      ></p>
                    </div>
                  )}

                  <div className="flex justify-start items-center my-2">
                    <BuildingOffice2Icon className="h-4 w-4 text-orange-600 mx-2" />
                    <p className="text-xs font-bold">
                      BU Team Size:{" "}
                      <span className="font-normal text-gray-400">
                        {item?.organizationBUTeamSize || "Not defined"}
                      </span>
                    </p>
                  </div>

                  <div className="flex justify-start items-center my-2">
                    <BuildingOffice2Icon className="h-4 w-4 text-orange-600 mx-2" />
                    <p className="text-xs font-bold">
                      BU Revenue Size:{" "}
                      <span className="font-normal text-gray-400">
                        {item?.organizationBURevenueSize || "Not defined"}
                      </span>
                    </p>
                  </div>

                  <div className="flex justify-start items-center my-2">
                    <CalendarDaysIcon className="h-4 w-4 text-orange-600 mx-2" />
                    <p className="text-xs font-bold">
                      Started:{" "}
                      <span className="font-normal text-gray-400">
                        {item?.organizationBUStartedDate
                          ? formatDate(item?.organizationBUStartedDate)
                          : "Not defined"}
                      </span>
                    </p>
                  </div>

                  {item?.organizationBULocations?.length > 0 && (
                    <div className="m-1 p-1">
                      <div className="flex justify-start items-center my-1">
                        <MapPinIcon className="h-4 w-4 text-orange-600 mx-1" />
                        <p className="text-xs font-bold">Locations</p>
                      </div>

                      {item.organizationBULocations.map((location, locIndex) => (
                        <div
                          key={locIndex}
                          className="rounded m-1 p-1 bg-gray-200 inline-block"
                        >
                          <p className="text-[10px] mx-1 px-1">
                            {location.location}
                          </p>
                        </div>
                      ))}
                    </div>
                  )}

                  {item?.organizationBUTargetAudience?.length > 0 && (
                    <div className="m-1 p-1">
                      <p className="text-xs font-bold m-1 p-1">
                        Target Audience
                      </p>

                      {item.organizationBUTargetAudience.map(
                        (audience, audIndex) => (
                          <div
                            key={audIndex}
                            className="rounded m-1 p-1 bg-orange-50 inline-block"
                          >
                            <p className="text-[10px] mx-1 px-1">
                              {audience.audience}
                            </p>
                          </div>
                        )
                      )}
                    </div>
                  )}

                  {item?.organizationBURecognitions?.length > 0 && (
                    <div className="m-1 p-1">
                      <p className="text-xs font-bold m-1 p-1">
                        Recognitions
                      </p>

                      {item.organizationBURecognitions.map(
                        (recognition, recIndex) => (
                          <div
                            key={recIndex}
                            className="rounded m-1 p-1 bg-gray-200"
                          >
                            <p className="text-[10px] mx-1 px-1">
                              {recognition.recognition}
                            </p>
                          </div>
                        )
                      )}
                    </div>
                  )}

                  {item?.organizationBUContributions?.length > 0 && (
                    <div className="m-1 p-1">
                      <p className="text-xs font-bold m-1 p-1">
                        Contributions
                      </p>

                      {item.organizationBUContributions.map(
                        (contribution, conIndex) => (
                          <div
                            key={conIndex}
                            className="rounded m-1 p-1 bg-gray-200"
                          >
                            <p className="text-[10px] mx-1 px-1">
                              {contribution.contribution}
                            </p>
                          </div>
                        )
                      )}
                    </div>
                  )}

                  {item?.organizationBUOutro && (
                    <div className="bg-gray-50 rounded text-xs mx-2 px-2 my-1 py-1">
                      <p
                        className="m-1 p-1"
                        dangerouslySetInnerHTML={createMarkup(
                          item?.organizationBUOutro
                        )}
                      ></p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  )
}

const formatDate = (dateString) => {
  const date = new Date(dateString)
  const options = { month: "long", year: "numeric" }
  const day = date.getDate()

  const ordinal = (n) => {
    let s = ["th", "st", "nd", "rd"],
      v = n % 100
    return s[(v - 20) % 10] || s[v] || s[0]
  }

  return (
    <>
      {day}
      <sup>{ordinal(day)}</sup>{" "}
      {new Intl.DateTimeFormat("en-GB", options).format(date)}
    </>
  )
}

export default EtOrganizationBUList